/**
 * Resolving a user id to a name.
 *
 * Several places (comments, partner activity) receive a bare `actor_id` /
 * `author_id` and must not print it. One shared query key means the workspace's
 * people are fetched once and reused everywhere, and a long `staleTime` because
 * the roster of a two-person workspace does not change during a session.
 */

import { useQuery } from "@tanstack/react-query";

import { Users } from "@/api/resources";
import type { UserOut } from "@/api/types";

export const PEOPLE_QUERY_KEY = ["users"] as const;

export function usePeople() {
  return useQuery({
    queryKey: PEOPLE_QUERY_KEY,
    queryFn: Users.list,
    staleTime: 10 * 60_000,
  });
}

/**
 * A name for `id`, or null when the roster has not arrived or the user is not
 * in it (a deactivated account still owns their old comments). Callers show the
 * timestamp alone in that case — never the raw UUID.
 */
export function nameOf(people: UserOut[] | undefined, id: string | null | undefined): string | null {
  if (!id || !people) return null;
  return people.find((u) => u.id === id)?.display_name ?? null;
}

/** First letter for the avatar-initial treatment (§8, Profile). */
export function initialOf(name: string): string {
  return name.trim().charAt(0).toUpperCase() || "?";
}
